import { useMemo } from "react";

import { MenuProps } from "antd/es/menu";
import { addDays, addMinutes, getHours, getMinutes, isAfter, startOfDay } from "date-fns";
import { DisabledTimes } from "rc-picker/lib/interface";

type DisabledTimeFn = (date: Date) => DisabledTimes;

import { useConfigProvider } from "../../ConfigProvider";

import { block } from "./consts";

function* intervalsGenerator(
  startDate: Date,
  intervalSizeInMinutes: number
): Generator<Date, void, never> {
  let date = startDate;
  const nextDay = addDays(startDate, 1);

  while (!isAfter(date, nextDay)) {
    yield date;
    date = addMinutes(date, intervalSizeInMinutes);
  }
}

const isTimeDisabled = (date: Date, disabledTime?: DisabledTimeFn): boolean => {
  if (!disabledTime) {
    return false;
  }
  const { disabledHours, disabledMinutes } = disabledTime(date);
  const hour = getHours(date);
  if (disabledHours?.().includes(hour)) {
    return true;
  }
  if (disabledMinutes?.(hour).includes(getMinutes(date))) {
    return true;
  }
  return false;
};

const useTimeMenu = (
  stepMinutes: number,
  onChange: (date: Date) => void,
  value: Date | null | undefined,
  timeFormat: string,
  disabledTime?: DisabledTimeFn
): MenuProps => {
  const { formatDate } = useConfigProvider();
  const selectedKeys = useMemo(
    () => (value ? [formatDate(value, "PPp")] : []),
    [formatDate, value]
  );

  return useMemo(() => {
    const baseDate = startOfDay(new Date());
    const items: MenuProps["items"] = Array.from(intervalsGenerator(baseDate, stepMinutes))
      .filter((date) => !isTimeDisabled(date, disabledTime))
      .map((date) => ({
        key: formatDate(date, "PPp"),
        label: formatDate(date, timeFormat),
        onClick: () => onChange(date),
      }));

    return { className: block("dropdown"), items, selectedKeys };
  }, [disabledTime, formatDate, stepMinutes, onChange, selectedKeys, timeFormat]);
};

export { isTimeDisabled };
export default useTimeMenu;
